import {EventContentRepository} from "./content_interface";
import {
  Content,
  ContentAnnotation,
  ContentComplement,
  Question,
  QuestionStatus, ScormCheckpoint
} from "../../../presentation/views/content/helpers";
import {wrapErrorHandler} from "../../utils/wrap_error_handler";
import http from "../../http";
import {Instructor} from "../../../../domain/entities/entities";


/** return the path to access the contents resources. */
const contentsPath = (eventID: number, classID: number, contentID?: number): string => {
  const rootPath = `/api/decola/events/${eventID}/class/${classID}/contents`
  if (contentID) {
    return `${rootPath}/${contentID}`;
  }
  return rootPath;
}

class EventContentRepositoryImpl implements EventContentRepository {
  async getAllContents(eventID: number, classID: number): Promise<Content[]> {
    return await wrapErrorHandler<Promise<Content[]>>(async () => {
      const {data} = await http.get(contentsPath(eventID, classID));
      return data;
    });
  }

  async getContentByID(eventID: number, classID: number, contentID: number): Promise<Content> {
    return await wrapErrorHandler<Promise<Content>>(async () => {
      const {data} = await http.get(contentsPath(eventID, classID, contentID));
      return data;
    });
  }

  async initializeContent(eventID: number, classID: number, contentID: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/initialize`);
      return data;
    });
  }

  async getAllComplements(eventID: number, classID: number, contentID: any): Promise<ContentComplement[]> {
    return await wrapErrorHandler<Promise<ContentComplement[]>>(async () => {
      const {data} = await http.get(`${contentsPath(eventID, classID, contentID)}/complements`);
      return data;
    });
  }

  async getContentInstructors(eventID: number, classID: number, contentID: any): Promise<Instructor[]> {
    return await wrapErrorHandler<Promise<Instructor[]>>(async () => {
      const {data} = await http.get(`${contentsPath(eventID, classID, contentID)}/instructors`);
      return data;
    });
  }

  async createContentAnnotation(eventID: number, classID: number, contentID: any, annotation): Promise<ContentAnnotation> {
    return await wrapErrorHandler<Promise<ContentAnnotation>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/notes`, annotation);
      return data;
    });
  }

  async getAllContentAnnotations(eventID: number, classID: number, contentID: any): Promise<ContentAnnotation[]> {
    return await wrapErrorHandler<Promise<ContentAnnotation[]>>(async () => {
      const {data} = await http.get(`${contentsPath(eventID, classID, contentID)}/notes`);
      return data;
    });
  }

  async updateContentAnnotation(eventID: number, classID: number, contentID: number, annotation: ContentAnnotation): Promise<ContentAnnotation> {
    return await wrapErrorHandler<Promise<ContentAnnotation>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/notes/${annotation.id}`, annotation);
      return data;
    });
  }

  async deleteContentAnnotation(eventID: number, classID: number, contentID: number, annotationID: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const {data} = await http.delete(`${contentsPath(eventID, classID, contentID)}/notes/${annotationID}`);
      return data;
    });
  };

  async saveCheckpointSeconds(eventID: number, classID: number, contentID: number, timestamp: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/checkpoints`, timestamp);
      return data;
    });
  }

  async saveCheckpointSCORM(eventID: number, classID: number, contentID: number, scormCheckpoint: ScormCheckpoint): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/scorm/checkpoints`, scormCheckpoint);
      return data;
    });
  }

  async saveProgress(eventID: number, classID: number, contentID: number, speedRate: number): Promise<number> {
    return await wrapErrorHandler<Promise<number>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/progress`, speedRate);
      return data;
    });
  }

  async generateQuestionnaire(eventID: number, classID: number, contentID: number): Promise<number> {
    return await wrapErrorHandler<Promise<number>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/questionnaires`);
      return data;
    });
  }

  async getQuestionByPage(eventID: number, classID: number, contentID: number, questionnaireID: number, page: number): Promise<Question> {
    return await wrapErrorHandler<Promise<Question>>(async () => {
      const {data} = await http.get(`${contentsPath(eventID, classID, contentID)}/questionnaires/${questionnaireID}/pages/${page}`);
      return data;
    });
  }

  async replyQuestionByPage(eventID: number, classID: number, contentID: number, questionnaireID: number, page: number, alternativeID: number): Promise<QuestionStatus> {
    return await wrapErrorHandler<Promise<QuestionStatus>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/questionnaires/${questionnaireID}/pages/${page}/alternatives/${alternativeID}`);
      return data;
    });
  }

  async replyDescriptiveQuestionByPage(eventID: number, classID: number, contentID: number, questionnaireID: number, page: number, answer: string): Promise<QuestionStatus> {
    return await wrapErrorHandler<Promise<QuestionStatus>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/questionnaires/${questionnaireID}/pages/${page}/descriptive`, {answer: answer});
      return data;
    });
  }

  async finishContent(eventID: number, classID: number, contentID: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const {data} = await http.post(`${contentsPath(eventID, classID, contentID)}/finish`);
      return data;
    });
  }
}

export default new EventContentRepositoryImpl() as EventContentRepository;
